/**
 * useKeyboardShortcuts — Global keyboard navigation for Fitxero admin.
 *
 * Shortcuts:
 *   /  → Focus search
 *   n  → New Member
 *   c  → Attendance (Check-in)
 *   p  → Payments
 *   r  → Reports
 *   t  → Trainers
 *   ?  → Show shortcuts overlay
 *   Escape → Close overlay
 */
import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

export function useKeyboardShortcuts({ searchRef } = {}) {
  const navigate = useNavigate();
  const [showHelp, setShowHelp] = useState(false);

  const handleKey = useCallback(
    e => {
      // Ignore if typing in an input/textarea/select/contenteditable
      const tag = e.target?.tagName?.toLowerCase();
      const isEditable =
        tag === 'input' ||
        tag === 'textarea' ||
        tag === 'select' ||
        e.target?.contentEditable === 'true';

      if (e.key === 'Escape') {
        setShowHelp(false);
        return;
      }
      if (e.key === '?' && !isEditable) {
        e.preventDefault();
        setShowHelp(v => !v);
        return;
      }
      if (isEditable) {
        return;
      }

      switch (e.key.toLowerCase()) {
        case '/':
          e.preventDefault();
          // Try to focus any visible search input
          const searchEl =
            searchRef?.current ||
            document.querySelector(
              'input[type="search"], input[placeholder*="Search"], input[placeholder*="search"]'
            );
          searchEl?.focus();
          break;
        case 'n':
          navigate('/admin/members/new');
          break;
        case 'c':
          navigate('/admin/attendance');
          break;
        case 'p':
          navigate('/admin/payments');
          break;
        case 'r':
          navigate('/admin/reports');
          break;
        case 't':
          navigate('/admin/trainers');
          break;
        case 'd':
          navigate('/admin/dashboard');
          break;
        default:
          break;
      }
    },
    [navigate, searchRef]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [handleKey]);

  return { showHelp, setShowHelp };
}

export const SHORTCUTS = [
  { key: 'N', desc: 'Add new member' },
  { key: 'C', desc: 'Go to attendance / check-in' },
  { key: 'P', desc: 'Go to payments' },
  { key: 'R', desc: 'Go to reports' },
  { key: 'T', desc: 'Go to trainers' },
  { key: 'D', desc: 'Go to dashboard' },
  { key: '/', desc: 'Focus search' },
  { key: '?', desc: 'Toggle this help panel' },
  { key: 'Esc', desc: 'Close overlays' },
];
